import React from 'react';
import type {
    ContextMenuConfig,
    ContextMenuItemConfig,
    ContextMenuItemNode,
    ContextMenuResolverContext,
    MenuNode,
} from './contextMenuTypes';

/** Resolves an item's translated or context-dependent label. */
const resolveLabel = <Context extends ContextMenuResolverContext>(
    item: ContextMenuItemConfig<Context>,
    context: Context,
): string => {
    if (item.localeKey === undefined) return item.label(context);

    const localeKey = typeof item.localeKey === 'function' ? item.localeKey(context) : item.localeKey;
    return context.translate(localeKey);
};

/** Narrows resolved item results after unavailable items have been removed. */
const isResolvedMenuItem = (item: ContextMenuItemNode | null): item is ContextMenuItemNode => item !== null;

/** Resolves configs recursively and inserts dividers only between non-empty sections. */
const resolveConfig = <Context extends ContextMenuResolverContext>(
    config: ContextMenuConfig<Context>,
    context: Context,
): MenuNode[] => {
    /** Resolves one available config item into the shared menu model. */
    const resolveMenuItem = (item: ContextMenuItemConfig<Context>): ContextMenuItemNode | null => {
        if (item.availabilityPredicate && !item.availabilityPredicate(context)) return null;

        const label = resolveLabel(item, context);
        const baseNode: ContextMenuItemNode = {
            type: 'item',
            id: item.id,
            label,
            leading: item.icon,
            shortcut: typeof item.shortcut === 'function' ? item.shortcut(context) : item.shortcut,
            isSelected: item.selectedPredicate?.(context),
            isDisabled: item.disabledPredicate?.(context) ?? item.isDisabled,
        };

        if (item.type === 'action') {
            return {
                ...baseNode,
                // A ticked action is a toggle, so leave the menu open to be flipped again.
                keepOpenOnSelect: !!item.selectedPredicate,
                onAction: () => item.onAction(context),
            };
        }

        if (item.type === 'custom') {
            return {
                ...baseNode,
                customContent: React.createElement(item.component, {
                    context,
                    id: item.id,
                    label,
                    isSelected: baseNode.isSelected,
                }),
                onAction: item.onAction ? () => item.onAction?.(context) : undefined,
            };
        }

        const children = resolveConfig(item.children, context);
        return children.length ? { ...baseNode, children } : null;
    };

    const sections = config
        .map((section) => {
            const items = typeof section.items === 'function' ? section.items(context) : section.items;
            return {
                id: section.id,
                items: items.map(resolveMenuItem).filter(isResolvedMenuItem),
            };
        })
        .filter((section) => section.items.length > 0);

    return sections.flatMap((section, index) =>
        index === 0 ? section.items : [{ type: 'divider', id: `divider-before-${section.id}` }, ...section.items],
    );
};

/** Produces the shared menu model from static config and context. */
export const resolveMenuItems = <Context extends ContextMenuResolverContext>(
    config: ContextMenuConfig<Context>,
    context: Context,
): MenuNode[] => resolveConfig(config, context);
